import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  AplicacaoStatusProcessando,
  NotificacoesResponse,
  Usuario,
} from '../../@types';
import NotificacoesService from '../../services/notificacoes';
import UsuariosService from '../../services/usuarios';

interface PendingApprovesStore {
  notifications: NotificacoesResponse;
  usuariosPorPropriedade: Record<number, Usuario[]>;
  isLoading: boolean;
  hasLoaded: boolean;
  loadError: boolean;
  textInput: string;
  setTextInput: (text: string) => void;
  updateItemStatusProcessando: (
    id: number,
    type: keyof Omit<NotificacoesResponse, 'pendentes'>,
    status: AplicacaoStatusProcessando,
  ) => void;
  loadNotifications: () => Promise<void>;
  refreshAproves: () => Promise<void>;
  clearFilters: () => void;
}

const initialFiltersState = {
  textInput: '',
};

const emptyNotifications: NotificacoesResponse = {
  recomendacoes: [],
  aplicacoes: [],
  aqilaRecomendacoes: [],
  pendentes: 0,
};

const initialState = {
  notifications: emptyNotifications,
  usuariosPorPropriedade: {} as Record<number, Usuario[]>,
  isLoading: false,
  hasLoaded: false,
  loadError: false,
  ...initialFiltersState,
};

const usePendingApprovesStore = create<PendingApprovesStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      setTextInput: (text) => set({ textInput: text }),
      updateItemStatusProcessando: (id, type, status) => {
        const { notifications } = get();
        const array = notifications[type];
        set({
          notifications: {
            ...notifications,
            [type]: array.map((item) =>
              item.id === id ? { ...item, statusProcessando: status } : item,
            ),
          },
        });
      },
      loadNotifications: async () => {
        set({ isLoading: true, loadError: false });

        try {
          const data = await NotificacoesService.list();

          const allItems = [
            ...data.recomendacoes,
            ...data.aplicacoes,
            ...data.aqilaRecomendacoes,
          ];

          const uniquePropriedadeIds = [
            ...new Set(allItems.map((item) => item.propriedade.id)),
          ];

          const usuariosEntries = await Promise.all(
            uniquePropriedadeIds.map(async (propId) => {
              const usuarios = await UsuariosService.listUsuariosAprovacao(
                String(propId),
              );
              return [propId, usuarios] as const;
            }),
          );

          const usuariosPorPropriedade = Object.fromEntries(usuariosEntries);

          set({
            notifications: data,
            usuariosPorPropriedade,
            hasLoaded: true,
            loadError: false,
          });
        } catch (error) {
          console.error(error);
          set({ loadError: true });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },
      refreshAproves: async () => {
        await get().loadNotifications();
      },
      clearFilters: () =>
        set({
          ...initialFiltersState,
          notifications: emptyNotifications,
          usuariosPorPropriedade: {},
          hasLoaded: false,
        }),
    }),
    {
      name: 'pending-aproves-filters',
      storage: createJSONStorage(() => window.localStorage),
      partialize: (state) => ({
        textInput: state.textInput,
      }),
    },
  ),
);

export default usePendingApprovesStore;
